<script setup lang="ts">
  import CalendarMonthGridView from '@/components/app/calendar/CalendarMonthGridView.vue'
  import CalendarTimeGridView from '@/components/app/calendar/CalendarTimeGridView.vue'
  import CalendarYearView from '@/components/app/calendar/CalendarYearView.vue'
  import type { CalendarViewId } from '@/constants/calendar/views'
  import type { CalendarGridEvent } from '@/types/calendar/grid'
  import type { CalendarEventTypeLabels } from '@/utils/calendar/event-display'
  import { startOfDay } from '@/utils/calendar/date'

  const props = defineProps<{
    selectedDate: Date
    activeView: CalendarViewId
    today: Date
    weekdayLabels: string[]
    eventsByDate: Map<string, CalendarGridEvent[]>
    appointmentDates: Set<string>
    allDayLabel: string
    moreLabel: string
    eventTypeLabels: CalendarEventTypeLabels
    canDragEvents: boolean
  }>()

  const emit = defineEmits<{
    'update:selectedDate': [date: Date]
    'open-day-view': []
    'open-event': [event: CalendarGridEvent]
    'request-move-event': [value: { eventUuid: string; start: string; end: string }]
  }>()

  const onYearSelectDate = (date: Date): void => {
    emit('update:selectedDate', date)
    emit('open-day-view')
  }

  const onMonthSelectDate = (date: Date): void => {
    emit('update:selectedDate', startOfDay(date))
    emit('open-day-view')
  }
</script>

<template>
  <div class="flex min-h-0 flex-1 flex-col">
    <CalendarYearView
      v-if="activeView === 'year'"
      :selected-date="selectedDate"
      :today="today"
      :weekday-labels="weekdayLabels"
      :event-dates="appointmentDates"
      @select-date="onYearSelectDate"
    />
    <CalendarMonthGridView
      v-else-if="activeView === 'month'"
      :selected-date="selectedDate"
      :today="today"
      :events-by-date="eventsByDate"
      :weekday-labels="weekdayLabels"
      :more-label="moreLabel"
      :event-type-labels="eventTypeLabels"
      @select-date="onMonthSelectDate"
      @open-event="emit('open-event', $event)"
    />
    <CalendarTimeGridView
      v-else
      :selected-date="selectedDate"
      :active-view="activeView === 'week' ? 'week' : 'day'"
      :events-by-date="eventsByDate"
      :weekday-labels="weekdayLabels"
      :all-day-label="allDayLabel"
      :event-type-labels="eventTypeLabels"
      :can-drag-events="canDragEvents"
      @open-event="emit('open-event', $event)"
      @request-move-event="emit('request-move-event', $event)"
    />
  </div>
</template>
